import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import { BrowserAnalytics } from '@transcend-io/segment/browser';
import type {
  TrackingEvent,
  TrackingEventProperties,
  TranscendIndividualTraits,
} from '@transcend-io/segment/core';

import { useConsentManager } from '../consent-manager';
import { segmentEnv } from './env';
import { conditionalTrackLinkedInConversion } from './linkedin-insight';

export { trackDeferred } from './track-deferred';

const analytics = new BrowserAnalytics();
const AnalyticsContext = createContext<BrowserAnalytics>(analytics);

// Fire the initial page view event ASAP. This gets dequeued on analytics.core.load();
if (typeof window !== 'undefined') {
  analytics.core.page();
}

/**
 * Reads the HubSpot usertoken (`hubspotutk`) cookie value directly from
 * `document.cookie`. Returns undefined on the server or when the cookie is absent.
 */
const readHubspotUtkCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
};

// Derive the source-middleware type from the analytics instance so we don't depend
// on `@segment/analytics-next` directly (it's a transitive dependency of @transcend-io/segment).
type SourceMiddleware = Parameters<
  BrowserAnalytics['core']['addSourceMiddleware']
>[0];

// Attach the visitor's HubSpot usertoken to every event's context. The downstream
// Segment → HubSpot Cloud Mode (Actions) destination maps `context.hubspotutk` to the `utk` identifier.
const hubspotUtkSourceMiddleware: SourceMiddleware = ({ payload, next }) => {
  const utk = readHubspotUtkCookie();
  if (utk) {
    const context = (payload.obj.context ??= {}) as Record<string, unknown>;
    if (context.hubspotutk == null) {
      context.hubspotutk = utk;
    }
  }
  next(payload);
};

// Registered once per page load; guarded so React strict mode / re-renders don't double-register.
let hubspotUtkMiddlewareRegistered = false;
const registerHubspotUtkMiddleware = (instance: BrowserAnalytics): void => {
  if (hubspotUtkMiddlewareRegistered) return;
  hubspotUtkMiddlewareRegistered = true;
  void instance.core.addSourceMiddleware(hubspotUtkSourceMiddleware);
};

/**
 * A prior bug called identify(anonymousId), so some returning visitors have
 * ajs_user_id === ajs_anonymous_id. Clear the fake userId without reset() so
 * Segment keeps the anonymousId and can merge browsing into a later real identify.
 */
const clearStaleAnonymousUserId = async (
  instance: BrowserAnalytics,
): Promise<void> => {
  const user = await instance.core.user();
  const userId = user.id();
  const anonymousId = user.anonymousId();
  if (userId && anonymousId && userId === anonymousId) {
    user.id(null);
  }
};

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const { airgap } = useConsentManager();

  // Load analytics.js once consent (airgap) is available
  useEffect(() => {
    if (airgap) {
      const writeKey = segmentEnv.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
      analytics.core.load({ writeKey });
      registerHubspotUtkMiddleware(analytics);
      void clearStaleAnonymousUserId(analytics);
    }
  }, [airgap]);

  // Create `analytics.page` events
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      analytics.core.page();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook for Segment analytics
 */
export const useAnalytics = (): BrowserAnalytics =>
  useContext(AnalyticsContext);

/**
 * Hook for conversion events
 * Identifies, sends event to segment, sends event to linkedin
 */
export const useConversionEvent = () => {
  const analytics = useAnalytics();

  return useCallback(
    async <T extends TrackingEvent>(
      email: string,
      trackingEventName: T,
      properties?: TrackingEventProperties[T] | null,
    ) => {
      await analytics.identify(email, { email: email });
      analytics.track(trackingEventName, properties ?? undefined);

      conditionalTrackLinkedInConversion(trackingEventName);
    },
    [analytics],
  );
};

/**
 * Attach traits to the current anonymous (or already-identified) user without
 * setting userId. Never pass anonymousId as userId — that breaks anonymous→known
 * merging when a real identify() later changes userId.
 */
export const identifyWithoutId = async (traits: TranscendIndividualTraits) => {
  await clearStaleAnonymousUserId(analytics);
  await analytics.core.identify(null, traits, { context: { traits } });
};
